// Content entry add/edit modal. Mirrors the columns of the BWM Content
// Manual Log spec: title, pillar, status, primary keyword + KW data,
// search intent, related keywords, PAA, AEO target, angle, word count,
// internal links, UTM, notes.

import { useState, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { PILLARS, CONTENT_STATUSES, POST_TYPES, SEARCH_INTENTS } from './pillars';

const inputClass = 'w-full px-3 py-2 text-[12px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue';
const labelClass = 'block text-[10px] font-bold uppercase tracking-wider text-navy mb-1';

function asArray(text) {
  if (!text) return [];
  return text.split('\n').map(s => s.trim()).filter(Boolean);
}

function arrayToText(arr) {
  return Array.isArray(arr) ? arr.join('\n') : '';
}

export default function ContentEntryModal({ entry, onClose }) {
  const { saveOne } = useContent();
  const isEditing = !!entry?.id;

  const [form, setForm] = useState(() => ({
    title: entry?.title || '',
    slug: entry?.slug || '',
    url: entry?.url || '',
    pillar: entry?.pillar || 'tax_planning',
    postType: entry?.postType || 'educational',
    status: entry?.status || 'idea',
    publishDate: entry?.publishDate || '',
    primaryKeyword: entry?.primaryKeyword || '',
    keywordVolume: entry?.keywordVolume ?? '',
    keywordDifficulty: entry?.keywordDifficulty ?? '',
    searchIntent: entry?.searchIntent || 'informational',
    relatedKeywordsText: arrayToText(entry?.relatedKeywords),
    peopleAlsoAskText: arrayToText(entry?.peopleAlsoAsk),
    aeoTarget: !!entry?.aeoTarget,
    angle: entry?.angle || '',
    wordCount: entry?.wordCount ?? '',
    internalLinksOut: entry?.internalLinksOut ?? 0,
    internalLinksIn: entry?.internalLinksIn ?? 0,
    utmCampaign: entry?.utmCampaign || '',
    notes: entry?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function update(k, v) { setForm(prev => ({ ...prev, [k]: v })); }

  // Auto-derive slug + UTM campaign from title when those fields are empty
  // (only on title change, only if user hasn't manually edited them).
  function handleTitleChange(v) {
    const slug = form.slug || slugify(v);
    const utm = form.utmCampaign || slug;
    setForm(prev => ({ ...prev, title: v, slug, utmCampaign: utm }));
  }

  async function handleSubmit() {
    if (!form.title.trim()) { setErr('Title is required'); return; }
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        ...(entry?.id ? { id: entry.id } : {}),
        title: form.title.trim(),
        slug: form.slug.trim() || null,
        url: form.url.trim() || null,
        pillar: form.pillar,
        postType: form.postType,
        status: form.status,
        publishDate: form.publishDate || null,
        primaryKeyword: form.primaryKeyword.trim() || null,
        keywordVolume: form.keywordVolume === '' ? null : Number(form.keywordVolume),
        keywordDifficulty: form.keywordDifficulty === '' ? null : Number(form.keywordDifficulty),
        searchIntent: form.searchIntent,
        relatedKeywords: asArray(form.relatedKeywordsText),
        peopleAlsoAsk: asArray(form.peopleAlsoAskText),
        aeoTarget: form.aeoTarget,
        angle: form.angle.trim() || null,
        wordCount: form.wordCount === '' ? null : Number(form.wordCount),
        internalLinksOut: Number(form.internalLinksOut) || 0,
        internalLinksIn: Number(form.internalLinksIn) || 0,
        utmCampaign: form.utmCampaign.trim() || null,
        notes: form.notes.trim() || null,
      };
      await saveOne(payload);
      onClose?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-[60] p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-[4px] shadow-[0_8px_30px_rgba(0,0,0,0.18)] w-full max-w-[820px] my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-bone/40 rounded-t-[4px]">
          <h3 className="text-[14px] font-bold text-navy">{isEditing ? 'Edit Content Entry' : 'Add Content Entry'}</h3>
          <button onClick={onClose} className="text-[20px] text-[#888] hover:text-navy leading-none px-1">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Row 1: Title, post type, status */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-7">
              <label className={labelClass}>Title</label>
              <input value={form.title} onChange={e => handleTitleChange(e.target.value)} className={inputClass} placeholder="How an Active Trader Cut a $634K Tax Bill..." />
            </div>
            <div className="col-span-3">
              <label className={labelClass}>Post Type</label>
              <select value={form.postType} onChange={e => update('postType', e.target.value)} className={inputClass}>
                {POST_TYPES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Status</label>
              <select value={form.status} onChange={e => update('status', e.target.value)} className={inputClass}>
                {CONTENT_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Pillar, publish date, slug, URL */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-4">
              <label className={labelClass}>Pillar</label>
              <select value={form.pillar} onChange={e => update('pillar', e.target.value)} className={inputClass}>
                {PILLARS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div className="col-span-3">
              <label className={labelClass}>Publish Date</label>
              <input type="date" value={form.publishDate} onChange={e => update('publishDate', e.target.value)} className={inputClass} />
            </div>
            <div className="col-span-5">
              <label className={labelClass}>Slug</label>
              <input value={form.slug} onChange={e => update('slug', e.target.value)} className={inputClass + ' font-mono text-[11px]'} placeholder="tax-planning-vs-tax-preparation" />
            </div>
          </div>

          <div>
            <label className={labelClass}>Full URL (once published)</label>
            <input value={form.url} onChange={e => update('url', e.target.value)} className={inputClass + ' font-mono text-[11px]'} placeholder="https://bullogicwealth.com/articles/..." />
          </div>

          {/* Discipline 1: Search intent + keyword pre-write */}
          <div className="border-t border-gray-200 pt-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-deep-teal mb-3">Discipline 1 — Search Intent + Keyword Pre-Write</div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-5">
                <label className={labelClass}>Primary Keyword</label>
                <input value={form.primaryKeyword} onChange={e => update('primaryKeyword', e.target.value)} className={inputClass} placeholder="trader tax status s-corp" />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>Volume / mo</label>
                <input type="number" value={form.keywordVolume} onChange={e => update('keywordVolume', e.target.value)} className={inputClass} />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>KD</label>
                <input type="number" value={form.keywordDifficulty} onChange={e => update('keywordDifficulty', e.target.value)} className={inputClass} />
              </div>
              <div className="col-span-3">
                <label className={labelClass}>Search Intent</label>
                <select value={form.searchIntent} onChange={e => update('searchIntent', e.target.value)} className={inputClass}>
                  {SEARCH_INTENTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className={labelClass}>Related Keywords (one per line)</label>
                <textarea value={form.relatedKeywordsText} onChange={e => update('relatedKeywordsText', e.target.value)} rows={4} className={inputClass + ' font-mono text-[11px]'} />
              </div>
              <div>
                <label className={labelClass}>People Also Ask (one per line)</label>
                <textarea value={form.peopleAlsoAskText} onChange={e => update('peopleAlsoAskText', e.target.value)} rows={4} className={inputClass + ' font-mono text-[11px]'} />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 mt-3">
              <div className="col-span-3">
                <label className={labelClass}>AEO Target?</label>
                <label className="flex items-center gap-2 mt-2 text-[12px]">
                  <input type="checkbox" checked={form.aeoTarget} onChange={e => update('aeoTarget', e.target.checked)} />
                  <span>AI Overview present in SERP</span>
                </label>
              </div>
              <div className="col-span-9">
                <label className={labelClass}>Angle (one sentence)</label>
                <input value={form.angle} onChange={e => update('angle', e.target.value)} className={inputClass} placeholder="The unique take only Bullogic can offer" />
              </div>
            </div>
          </div>

          {/* Discipline 3 + production data */}
          <div className="border-t border-gray-200 pt-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-deep-teal mb-3">Production + Internal Links</div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-3">
                <label className={labelClass}>Word Count</label>
                <input type="number" value={form.wordCount} onChange={e => update('wordCount', e.target.value)} className={inputClass} />
              </div>
              <div className="col-span-3">
                <label className={labelClass}>Internal Links Out</label>
                <input type="number" value={form.internalLinksOut} onChange={e => update('internalLinksOut', e.target.value)} className={inputClass} />
              </div>
              <div className="col-span-3">
                <label className={labelClass}>Internal Links In</label>
                <input type="number" value={form.internalLinksIn} onChange={e => update('internalLinksIn', e.target.value)} className={inputClass} />
              </div>
              <div className="col-span-3">
                <label className={labelClass}>UTM Campaign</label>
                <input value={form.utmCampaign} onChange={e => update('utmCampaign', e.target.value)} className={inputClass + ' font-mono text-[11px]'} />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelClass}>Notes</label>
            <textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows={3} className={inputClass} />
          </div>

          {err && <div className="text-[11px] text-rose">{err}</div>}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="px-3 py-1.5 text-[12px] font-bold text-[#888] bg-gray-100 hover:bg-gray-200 rounded-[3px]">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`px-3 py-1.5 text-[12px] font-bold rounded-[3px] ${saving ? 'bg-gray-200 text-gray-400' : 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'}`}
            >
              {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Entry')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}
